'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DropzoneOptions } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';

import { useToast } from '@/components/ui/use-toast';
import {
  FILE_VALIDATION,
  getDropzoneAcceptObject,
  isFileTypeAllowed,
  MEDIA_GALLERY_FILE_TYPES,
  validateFileSize,
} from '@/constants/variables';
import { useGalleryUpload } from '@/hooks/mutations';
import { getVideoDuration } from '@/hooks/utils/useFileUpload';

/**
 * Validates a file's size and, if it's a video, its duration
 */
async function validateFile(file: File): Promise<{ valid: boolean; error?: string }> {
  // Check file size
  const sizeValidation = validateFileSize(file, FILE_VALIDATION.mediaGallery.maxSizeMB);
  if (sizeValidation.valid === false) {
    return sizeValidation;
  }

  // Check if file type is allowed
  if (isFileTypeAllowed(file) === false) {
    return {
      valid: false,
      error: `File type is not allowed. Allowed types: ${MEDIA_GALLERY_FILE_TYPES.extensions.join(', ')}`,
    };
  }

  // Check video duration if it's a video file
  if (file.type.startsWith('video/')) {
    try {
      const duration = await getVideoDuration(file);
      if (duration > FILE_VALIDATION.mediaGallery.maxVideoDurationSeconds) {
        return {
          valid: false,
          error: `Video duration (${duration.toFixed(1)} seconds) exceeds the maximum allowed (${FILE_VALIDATION.mediaGallery.maxVideoDurationSeconds} seconds)`,
        };
      }
    } catch (error) {
      console.error('Error checking video duration:', error);
      // If we can't determine duration, we'll still allow the upload
    }
  }

  return { valid: true };
}

/**
 * Custom hook for file upload with drag and drop functionality
 * Uses react-dropzone and integrates with gallery upload
 */
function useDropzoneUpload({
  onUploadSuccess,
  onUploadStart,
  onUploadProgress,
  onComplete,
  allowMultiple: _allowMultiple = true,
  pollingStrategy,
  dropzoneOptions = {},
}: {
  onUploadSuccess?: (file?: File) => Promise<unknown>;
  onUploadStart?: (file: File) => void;
  onUploadProgress?: (progress: number) => void;
  onComplete?: () => Promise<unknown>;
  allowMultiple?: boolean;
  pollingStrategy?: 'single' | 'list' | false;
  dropzoneOptions?: Partial<DropzoneOptions>;
} = {}) {
  const { toast } = useToast();
  const [uploadErrors, setUploadErrors] = useState<Array<{ name: string; error: unknown }>>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({ total: 0, completed: 0, failed: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const translate = useTranslations();

  // Gallery upload functionality - pass all configuration options to useGalleryUpload
  const { uploadFile, isUploading } = useGalleryUpload({
    // Pass the onComplete callback
    onUploadSuccess: onComplete,
    // Pass the polling strategy (convert false to undefined)
    pollingStrategy: pollingStrategy === false ? undefined : pollingStrategy,
  });

  // Create accept object for dropzone (mapping of MIME types to extensions)
  const acceptObject = useMemo(() => getDropzoneAcceptObject(), []);

  // Handle file uploads when files are dropped
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    // Reset state
    setUploadErrors([]);
    setUploadProgress(0);
    setIsProcessing(true);
    setUploadStats({ total: acceptedFiles.length, completed: 0, failed: 0 });

    // Show initial toast
    toast({
      title: translate('upload.status.uploadStarted'),
      description: translate('upload.status.processing', { count: String(acceptedFiles.length) }),
      variant: 'success',
    });

    // CRITICAL: Call onUploadStart immediately for the first file
    // This ensures UI shows loading state before any validation or processing
    if (acceptedFiles.length > 0 && onUploadStart !== undefined) {
      onUploadStart(acceptedFiles[0] as File);
    }

    let successCount = 0;
    let errorCount = 0;
    const errors: Array<{ name: string; error: unknown }> = [];

    // Upload each file
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      if (file === undefined || file === null) {
        continue;
      }

      try {
        // Validate file before uploading
        const validation = await validateFile(file);
        if (validation.valid === false) {
          // Show validation error toast with specific message
          toast({
            title: translate('upload.status.validationError', { filename: file.name }),
            description: validation.error || translate('upload.dropzone.fileValidationFailed'),
            variant: 'error',
          });

          throw new Error(validation.error);
        }

        await uploadFile({ file });
        successCount++;

        // Update progress
        const newProgress = Math.round(((i + 1) / acceptedFiles.length) * 100);
        setUploadProgress(newProgress);

        // Call progress callback if provided
        if (onUploadProgress !== undefined) {
          onUploadProgress(newProgress);
        }

        setUploadStats(prev => ({ ...prev, completed: prev.completed + 1 }));
      } catch (error) {
        errorCount++;
        errors.push({ name: file.name, error });

        // Don't show toast for validation errors (already shown above)
        if (typeof error === 'object' && error !== null && 'message' in error
          && !(error.message?.toString().includes('exceeds the maximum allowed'))) {
          // Show toast for other upload errors
          toast({
            title: translate('upload.status.failedToUpload', { filename: file.name }),
            description: error instanceof Error ? error.message : translate('upload.status.unknownError'),
            variant: 'error',
          });
        }

        console.error(`Failed to upload file ${file.name}`, error);
        setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      }
    }

    // Set final errors
    if (errors.length > 0) {
      setUploadErrors(errors);
    }

    // Show completion toast only if there were successful uploads
    if (successCount > 0) {
      toast({
        title: translate('upload.status.uploadComplete'),
        description: errorCount > 0
          ? translate('upload.status.successfullyUploadedWithFailed', { successCount: successCount.toString(), errorCount: errorCount.toString() })
          : translate('upload.status.successfullyUploaded', { count: String(successCount) }),
        variant: successCount === acceptedFiles.length ? 'success' : 'error',
      });
    } else if (errorCount > 0 && errors.length > 0) {
      // Only show this if we haven't shown individual error toasts already
      const validationErrors = errors.filter(e =>
        typeof e.error === 'object' && e.error !== null && 'message' in e.error
        && e.error.message?.toString().includes('exceeds the maximum allowed'),
      );

      if (validationErrors.length === 0) {
        // Find the first error to display
        const firstError = errors.find(e => e.error !== undefined);
        const errorMessage = firstError && typeof firstError.error === 'object' && firstError.error !== null && 'message' in firstError.error
          ? String(firstError.error.message)
          : translate('upload.status.genericFailure', { count: String(errorCount) });

        toast({
          title: translate('upload.status.uploadFailed'),
          description: errorMessage,
          variant: 'error',
        });
      }
    }

    // Reset processing state
    setIsProcessing(false);

    // Guard against infinite loops - only call onUploadSuccess if:
    // 1. We had successful uploads
    // 2. The callback is defined
    // 3. We're not already processing
    if (successCount > 0 && onUploadSuccess !== undefined && !isUploading) {
      try {
        // Pass the last successfully uploaded file as a convenience
        const lastSuccessfulFile = acceptedFiles.length === 1 ? acceptedFiles[0] : undefined;
        await onUploadSuccess(lastSuccessfulFile);
      } catch (error) {
        console.error('Error in onUploadSuccess callback:', error);
      }
    }
  }, [uploadFile, onUploadSuccess, onUploadStart, onUploadProgress, toast, translate, isUploading]);

  // Create the dropzone instance
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptObject,
    ...dropzoneOptions,
  });

  // Reset isDragging if user presses escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDragging === true) {
        dragCounterRef.current = 0;
        setIsDragging(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDragging]);

  // Return all the necessary props and state
  return {
    dropzoneProps: getRootProps(),
    getInputProps,
    isDragActive,
    isDragging,
    uploadProgress,
    uploadStats,
    isProcessing,
    isUploading,
    uploadErrors,
  };
}

export default useDropzoneUpload;
